import { useRef, useEffect, useState, useCallback, useLayoutEffect } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';
import { useAppSelector } from '../app/hooks';
import { getMcaDocApiData } from '../store/investor/service';
import { usePolling } from './commonUtil';
import { DATA_FETCH_STATUS } from '../store/dashboard/constants';
import { phoneIconGif } from '../assets/hostedassets';
import { normalizeNameKey, resolveCompanyDisplayName } from './utils';
import { DEFAULT_DOCUMENT_TITLE } from './constants/commonConstants';
import { prefersReducedMotion } from './prefersReducedMotion';
import { createResizeObserver } from './createResizeObserver';

/**
 * A custom useEffect hook that only triggers on updates, not on initial mount
 * @param {Function} effect
 * @param {Array<any>} dependencies
 */

/**
 * Positions a dropdown using `position: fixed` relative to a trigger element.
 * Useful inside modals where absolute positioning fails.
 * Recalculates position on open and closes on outside scroll.
 *
 * @param isEnabled Enables or disables fixed dropdown behavior
 * @param isOpen Controls when position calculation and listeners are active
 * @param triggerRef Ref to the element the dropdown is aligned to
 * @param dropdownRef Optional ref to ignore scrolls inside the dropdown
 * @param onClose Callback fired when dropdown should close
 */

interface UseFixedDropdownInModalProps {
    isEnabled: boolean;
    isOpen: boolean;
    triggerRef: React.RefObject<HTMLElement>;
    dropdownRef?: React.RefObject<HTMLElement>;
    onClose?: () => void;
}

const rowHighlightTimers = new WeakMap<HTMLElement, ReturnType<typeof setTimeout>>();
export const ROW_FOCUS_HIGHLIGHT_CLASS = 'RowFocused';
export const ROW_FOCUS_HIGHLIGHT_MS = 2500;

const DROPDOWN_VIEWPORT_EDGE_MARGIN = 8;

export const useFixedDropdownInModal = ({ isEnabled, isOpen, triggerRef, dropdownRef, onClose }: UseFixedDropdownInModalProps) => {
    const [position, setPosition] = useState({
        top: 0,
        left: 0,
        width: 0,
    });

    // Below the trigger by default; flips above when it would overflow and there's more room above.
    const calculatePosition = useCallback(() => {
        if (!isEnabled || !triggerRef.current) return;

        const rect = triggerRef.current.getBoundingClientRect();
        const left = rect.left + window.scrollX;
        const width = rect.width;
        let top = rect.bottom + window.scrollY;

        const menuHeight = dropdownRef?.current?.getBoundingClientRect().height ?? 0;
        if (menuHeight > 0) {
            const spaceBelow = window.innerHeight - rect.bottom;
            const overflowsBelow = rect.bottom + menuHeight > window.innerHeight - DROPDOWN_VIEWPORT_EDGE_MARGIN;
            const moreRoomAbove = rect.top > spaceBelow;

            if (overflowsBelow && moreRoomAbove) {
                top = rect.top - menuHeight;
            }
        }

        setPosition({ top, left, width });
    }, [isEnabled, triggerRef, dropdownRef]);

    //  Handle scroll close logic

    const handleScrollClose = useCallback(
        (event: Event) => {
            const target = event.target as HTMLElement;

            if (dropdownRef?.current && dropdownRef.current.contains(target)) {
                return;
            }

            onClose?.();
        },
        [dropdownRef, onClose],
    );

    // Layout effect so the flip applies before paint.
    useLayoutEffect(() => {
        if (isEnabled && isOpen) {
            calculatePosition();
        }
    }, [isEnabled, isOpen, calculatePosition]);

    // Attach scroll listener only when required
    useEffect(() => {
        if (!isEnabled || !isOpen) return;

        document.addEventListener('scroll', handleScrollClose, true);

        return () => {
            document.removeEventListener('scroll', handleScrollClose, true);
        };
    }, [isEnabled, isOpen, handleScrollClose]);

    return position;
};
export function useUpdateEffect(effect: Function, dependencies: any[] = []) {
    const isInitialMount = useRef(true);

    useEffect(() => {
        if (isInitialMount.current) {
            isInitialMount.current = false;
        } else {
            return effect();
        }
    }, dependencies);
}

export const useApplyTheme = (uiStyle) => {
    useEffect(() => {
        if (uiStyle) {
            const root = document.documentElement;
            const buttons = uiStyle?.buttons;
            const tabs = uiStyle?.tabs;
            const texts = uiStyle?.texts;
            const backgrounds = uiStyle?.backgrounds;
            const borders = uiStyle?.borders;

            root.style.setProperty('--primary-button-bg', buttons?.primary?.backgroundColour);
            root.style.setProperty('--primary-button-text', buttons?.primary?.textColour);

            root.style.setProperty('--secondary-button-bg', buttons?.secondary?.backgroundColour);
            root.style.setProperty('--secondary-button-text', buttons?.secondary?.textColour);
            root.style.setProperty('--secondary-button-border', buttons?.secondary?.borderColour);

            root.style.setProperty('--tertiary-button-text', buttons?.tertiary?.textColour);

            root.style.setProperty('--tab-active-bg', tabs?.active?.backgroundColour);
            root.style.setProperty('--tab-active-text', tabs?.active?.textColour);

            root.style.setProperty('--tab-inactive-bg', tabs?.inactive?.backgroundColour);
            root.style.setProperty('--tab-inactive-text', tabs?.inactive?.textColour);

            root.style.setProperty('--primary-text-colour', texts?.primary?.textColour);

            root.style.setProperty('--primary-bg-colour', backgrounds?.primary?.backgroundColour);
            root.style.setProperty('--secondary-bg-colour', backgrounds?.secondary?.backgroundColour);

            root.style.setProperty('--primary-border-colour', borders?.primary?.borderColour);
        }
    }, [uiStyle]);
};

type CompanyNameFields = { assesseeOrgId?: string; displayName?: string | null; orgName?: string | null; organisationName?: string | null };

// RECUR-27990: sole writer of the browser tab title — org name when the URL path carries the loaded org's id, else the PNO name, never stale.
export const useDocumentTitle = () => {
    const { pathname } = useLocation();
    const openCompany = useAppSelector((state) => state.investor.selectedCompany) as CompanyNameFields | undefined;
    const pnoOrgName = useAppSelector((state) => state.user.domainMetadata?.orgName);
    const orgId = openCompany?.assesseeOrgId;
    const onThisOrgsPage = !!orgId && pathname.split('/').includes(orgId);
    const title = (onThisOrgsPage && resolveCompanyDisplayName(openCompany)) || pnoOrgName?.trim() || DEFAULT_DOCUMENT_TITLE;

    useEffect(() => {
        document.title = title;
    }, [title]);
};

interface McaLoaderData {
    isFetching: boolean;
    reportStatus: typeof DATA_FETCH_STATUS.PROCESSING | typeof DATA_FETCH_STATUS.PROCESSED;
    updatedAt: string;
}

export const useMcaPolling = (orgId: string) => {
    const [mcaDocData, setMcaDocData] = useState<McaLoaderData | null>(null);
    const transformMcaLoaderData = useCallback((data: any) => {
        if (data) {
            const transformedData = {
                isFetching: data?.mcaDocApiData?.isFetching,
                reportStatus: data?.reportStatusFromOrgMaster?.reportStatus?.auditedFinancialsStatus,

                updatedAt: data?.mcaDocApiData?.updatedAt,
            };
            // Bail out of the re-render when a 10s poll tick brings back an unchanged loader
            // state — returning the previous reference keeps consumers stable.
            setMcaDocData((prev) =>
                prev &&
                prev.isFetching === transformedData.isFetching &&
                prev.reportStatus === transformedData.reportStatus &&
                prev.updatedAt === transformedData.updatedAt
                    ? prev
                    : transformedData,
            );
        }
    }, []);

    const getMCaApiDataToPoll = useCallback(async () => {
        const res = await getMcaDocApiData({ orgId: orgId });
        return res.data?.responseBody;
    }, [orgId]);

    const handleMcaPollingSuccess = useCallback(
        (data: any) => {
            transformMcaLoaderData(data);
        },
        [transformMcaLoaderData],
    );

    const shouldContinueMcaPolling = useCallback(
        (data: any) =>
            data?.reportStatusFromOrgMaster?.reportStatus?.auditedFinancialsStatus === 'Processing' &&
            data?.mcaDocApiData?.isFetching === true,
        [],
    );

    usePolling({
        fetchData: getMCaApiDataToPoll,
        onSuccess: handleMcaPollingSuccess,
        shouldContinue: shouldContinueMcaPolling,
        interval: 10000,
    });

    return { mcaDocData };
};

/**
 * Custom hook to animate phone icon GIF by re-rendering it every 2 seconds
 * @returns {string} src - The phone icon GIF URL with timestamp for cache busting
 */
export const usePhoneIconGif = (duration: number = 2000) => {
    const [src, setSrc] = useState(phoneIconGif);

    useEffect(() => {
        const interval = setInterval(() => {
            setSrc(`${phoneIconGif}?${Date.now()}`);
        }, duration);

        return () => clearInterval(interval);
    }, []);

    return src;
};

/**
 * Marks a row and clears the mark ROW_FOCUS_HIGHLIGHT_MS later. Calling it again restarts that
 * clock, so a caller still nudging the row into place (see `useReanchorOnReflow`) can keep it
 * visibly marked until it settles instead of the reader watching the highlight expire mid-scroll.
 */
export const holdRowHighlight = (row: HTMLElement) => {
    clearTimeout(rowHighlightTimers.get(row));
    row.classList.add(ROW_FOCUS_HIGHLIGHT_CLASS);
    rowHighlightTimers.set(
        row,
        setTimeout(() => {
            rowHighlightTimers.delete(row);
            row.classList.remove(ROW_FOCUS_HIGHLIGHT_CLASS);
        }, ROW_FOCUS_HIGHLIGHT_MS),
    );
};

/**
 * Scrolls to + flashes a highlight on the row whose data attribute matches a URL search param.
 * Used to deep-link from Management Profile columns to a specific director row in the
 * destination tab (Related Parties, Legal Cases, etc.).
 *
 * The hook applies the GLOBAL `.RowFocused` class (defined in scss/index.scss) so destination
 * modules with their own sticky-column backgrounds can opt into the highlight via
 * `:global(.RowFocused)` selectors. Consumer is responsible for:
 *  - adding the `data-<attr>` attribute to each candidate row
 *  - normalising both the URL param value AND the row attribute via `normalizeNameKey`
 *  - calling this hook *after* data is rendered (`ready` flag)
 *
 * The hook clears the matched param (and `section`) from the URL once it settles — on a successful
 * focus AND on giving up — so re-renders don't loop, a dead deep link isn't left in a
 * copy-pasteable URL, and subsequent in-tab navigation isn't sticky. Giving up takes MAX_ATTEMPTS
 * searches of a list that HAS rows, or MAX_WAIT_MS wall-clock, whichever comes first: an empty
 * list means the destination is still loading, which is not evidence the target is absent.
 *
 * @param ready - Pass true once the destination list has rendered (e.g. data loaded, viewState === DATA)
 * @param paramKey - URL search-param key to read (e.g. 'directorName')
 * @param dataAttr - DOM data attribute on candidate rows (e.g. 'rp-director', 'lc-director')
 * @param fallbackParamKey - Optional secondary param + data attribute pair (e.g. DIN lookup if name miss)
 * @param fallbackDataAttr - Optional secondary data attribute matched against fallbackParamKey
 */
export const useFocusRowFromUrlParam = ({
    ready,
    paramKey,
    dataAttr,
    fallbackParamKey,
    fallbackDataAttr,
    clickOnMatch = false,
    extraParamKeys = [],
}: {
    ready: boolean;
    paramKey: string;
    dataAttr: string;
    fallbackParamKey?: string;
    fallbackDataAttr?: string;
    clickOnMatch?: boolean;
    /** Further params the caller's deep link carries, cleared together with the focus params. */
    extraParamKeys?: string[];
}) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const primary = searchParams.get(paramKey) ?? '';
    const fallback = fallbackParamKey ? searchParams.get(fallbackParamKey) ?? '' : '';

    useEffect(() => {
        if (!ready) return;
        if (!primary && !fallback) return;
        const primaryTarget = normalizeNameKey(primary);
        const fallbackTarget = fallback.trim();

        const POLL_INTERVAL_MS = 300;
        const MAX_ATTEMPTS = 17;
        const MAX_WAIT_MS = 15000;
        let attempts = 0;
        let pollId: number | undefined;
        const startedAt = Date.now();

        const hasCandidateRows = (): boolean =>
            !!document.querySelector(`[data-${dataAttr}]`) ||
            (!!fallbackDataAttr && !!document.querySelector(`[data-${fallbackDataAttr}]`));

        const findRow = (): HTMLElement | null => {
            if (fallbackTarget && fallbackDataAttr) {
                const r = document.querySelector(`[data-${fallbackDataAttr}="${CSS.escape(fallbackTarget)}"]`) as HTMLElement | null;
                if (r) return r;
            }
            if (primaryTarget) {
                return document.querySelector(`[data-${dataAttr}="${CSS.escape(primaryTarget)}"]`) as HTMLElement | null;
            }
            return null;
        };

        const clearFocusParams = () => {
            const next = new URLSearchParams(searchParams);
            next.delete(paramKey);
            if (fallbackParamKey) next.delete(fallbackParamKey);
            next.delete('section');
            extraParamKeys.forEach((key) => next.delete(key));
            setSearchParams(next, { replace: true });
        };

        const focusRow = (row: HTMLElement) => {
            row.scrollIntoView({ behavior: 'smooth', block: 'center' });
            holdRowHighlight(row);
            if (clickOnMatch) row.click();
            clearFocusParams();
        };

        const tryFocus = () => {
            const row = findRow();
            if (row) {
                focusRow(row);
                return;
            }
            if (hasCandidateRows()) attempts += 1;
            if (attempts < MAX_ATTEMPTS && Date.now() - startedAt < MAX_WAIT_MS) {
                pollId = window.setTimeout(tryFocus, POLL_INTERVAL_MS);
                return;
            }
            clearFocusParams();
        };

        const rafId = requestAnimationFrame(tryFocus);

        return () => {
            cancelAnimationFrame(rafId);
            if (pollId !== undefined) clearTimeout(pollId);
        };
    }, [ready, primary, fallback]);
};

/**
 * FLIP animation for a keyed list of rows.
 *
 * Usage:
 *   const { rowRef } = useFlipRows(items);
 *   <tr ref={rowRef(col.field)}>…</tr>
 *
 * Pattern mirrors ModuleRail.tsx — capture positions before render (via
 * useLayoutEffect reading prevRectsRef), compute delta, apply inverse
 * translateY, force reflow, then transition to zero.
 *
 * Respects prefers-reduced-motion: skips the transform, leaving the
 * highlight-only effect (applied by the caller via a CSS class).
 */
export function useFlipRows<T>(items: T[], enabled = true) {
    const rowRefs = useRef<Map<string, HTMLTableRowElement>>(new Map());
    const prevRectsRef = useRef<Map<string, DOMRect>>(new Map());

    useLayoutEffect(() => {
        if (!enabled) {
            rowRefs.current.forEach((el) => {
                el.style.transition = '';
            });
            prevRectsRef.current = new Map();
            return;
        }
        const prev = prevRectsRef.current;

        const next = new Map<string, DOMRect>();
        rowRefs.current.forEach((el, key) => {
            if (el && document.body.contains(el)) {
                next.set(key, el.getBoundingClientRect());
            }
        });

        if (!prefersReducedMotion()) {
            next.forEach((newRect, key) => {
                const oldRect = prev.get(key);
                const el = rowRefs.current.get(key);
                if (!oldRect || !el) return;
                const dy = oldRect.top - newRect.top;
                if (Math.abs(dy) < 2) return;
                el.style.transition = 'none';
                el.style.transform = `translateY(${dy}px)`;
                // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                el.offsetHeight;
                requestAnimationFrame(() => {
                    el.style.transition = 'transform 230ms ease-out';
                    el.style.transform = '';
                });
            });
        }

        prevRectsRef.current = next;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, enabled]);

    const rowRef = (key: string) => (el: HTMLTableRowElement | null) => {
        if (el) {
            rowRefs.current.set(key, el);
        } else {
            rowRefs.current.delete(key);
        }
    };

    return { rowRef };
}

/**
 * Measures an element's content width and keeps it current as the element resizes.
 *
 * For headers that must compact when a drawer narrows their CONTAINER — a viewport media query is
 * wrong in both directions there, because the viewport hasn't changed. Returns the width rather
 * than a boolean so one observer can serve several breakpoints on the same element.
 *
 * Returns a CALLBACK ref, not a ref object: the element it measures may mount after the first
 * render (a component that renders nothing until its data arrives), and a ref object's identity
 * never changes, so an effect keyed on it would run once against `null` and never observe anything.
 *
 * Width is 0 until the first measurement, and in environments without ResizeObserver.
 */
export function useElementWidth() {
    const [element, setElement] = useState<HTMLElement | null>(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!element) return undefined;
        const ro = createResizeObserver((entries) => setWidth(entries[0]?.contentRect.width ?? element.clientWidth));
        if (!ro) return undefined;
        ro.observe(element);
        return () => ro.disconnect();
    }, [element]);

    return [setElement, width] as const;
}
